import FilterListIcon from '@mui/icons-material/FilterList';
import VisibilityIcon from '@mui/icons-material/Visibility';
import {
  Badge,
  Box,
  CircularProgress,
  Drawer,
  IconButton,
  Tooltip,
  Typography,
  useTheme
} from "@mui/material";
import { useSnackbar } from "notistack";
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useConfig } from "../../contexts/ConfigContext";
import { useLayers } from "../../contexts/LayerContext";
import { useOptions } from "../../contexts/OptionsContext";
import { SelectionContext } from "../../contexts/SelectionContext";
import { useSpinner } from "../../contexts/SpinnerContext";
import { useUser } from "../../contexts/UserContext";
import { useUserInterface } from "../../contexts/UserInterfaceContext";
import { useAutoSearch } from "../../hooks/useAutoSearch";
import { ApiEndpoint, FlexibleDataItem, RelatedEntityItem } from "../../types";
// Replace data source-specific imports with generic adapter functions
import { fetchEndpointData, searchEndpoint, isEndpointEditable, findCorrectEndpointForItem, getDataItemName } from "../../utilities/dataSourceAdapters";
import { enqueueUniqueSnackbar } from "../../utilities/snackbarUtils";
import { CMButton } from "../common/CMButton";
import ErrorBoundary from "../common/ErrorBoundary";
import { UI_CONSTANTS } from "../../constants/uiConstants";
import ToggleButton from "../common/ToggleButton";
import DataGrid from "./DataGrid";
import DataItemDetails from "./DataItemDetails";
import EndpointButtons from "./EndpointButtons";
import FilterToolbar from "./FilterToolbar";
import ItemFormDialog from "./ItemFormDialog";
import MediaDrawer from "../media/MediaDrawer";

interface DataDrawerProps {
  onOpenBulkEdit?: (itemIds: string[]) => void;
  options?: {
    tableName?: string;
    initialSearchTerm?: string;
  };
}

const DataDrawer: React.FC<DataDrawerProps> = ({
  onOpenBulkEdit,
  options: passedOptions = {},
}) => {
  const config = useConfig();
  const theme = useTheme();
  const { layers, refreshLayer, setLayerVisibility, visibleLayers } = useLayers();
  const { refreshOptions } = useOptions();
  const [data, setData] = useState<FlexibleDataItem[]>([]);
  const [paginationMeta, setPaginationMeta] = useState<{
    count: number;
    next: string | null;
    previous: string | null;
  } | null>(null);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [serverSearchLoading, setServerSearchLoading] = useState(false);
  const [activeFilters, setActiveFilters] = useState<Record<string, string>>({});
  const [isMediaEndpoint, setIsMediaEndpoint] = useState(false);
  const [isFilterToolbarOpen, setIsFilterToolbarOpen] = useState(false);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [filteredCount, setFilteredCount] = useState(0);
  const [hiddenDueToImages, setHiddenDueToImages] = useState(0);
  const [viewMode, setViewMode] = useState<'cards' | 'table'>('cards');
  const [showRelatedItems, setShowRelatedItems] = useState(false);
  const { setSelectedEntity, selectedEntity, selectedEndpoint } = useContext(SelectionContext);

  // Extract related items from selectedEntity for DataGrid
  const relatedItemsData = useMemo(() => {
    console.log('[DataDrawer] Extracting related items data:', {
      hasSelectedEntity: !!selectedEntity,
      selectedEntityId: selectedEntity?.id,
      selectedEntityName: selectedEntity?.name,
      hasRelationships: !!selectedEntity?.relationships,
      relationshipKeys: selectedEntity?.relationships ? Object.keys(selectedEntity.relationships) : null
    });
    
    if (!selectedEntity?.relationships) {
      console.log('[DataDrawer] No relationships found for selected entity');
      return [];
    }
    
    const allRelatedItems: RelatedEntityItem[] = [];
    
    // Extract from related_entities - ADD CATEGORY METADATA
    if (selectedEntity.relationships.related_entities) {
      Object.entries(selectedEntity.relationships.related_entities).forEach(([category, items]) => {
        if (Array.isArray(items)) {
          console.log('[DataDrawer] Adding related_entities:', { category, count: items.length });
          // CRITICAL: Add category metadata to each item
          const itemsWithCategory = items.map((item: any) => ({
            ...item,
            category: category, // Add the category from the relationship structure
            relationshipType: 'related_entities'
          }));
          allRelatedItems.push(...itemsWithCategory);
        }
      });
    }
    
    // Extract from geo_entities - ADD CATEGORY METADATA
    if (selectedEntity.relationships.geo_entities) {
      Object.entries(selectedEntity.relationships.geo_entities).forEach(([category, items]) => {
        if (Array.isArray(items)) {
          console.log('[DataDrawer] Adding geo_entities:', { category, count: items.length });
          // CRITICAL: Add category metadata to each item
          const itemsWithCategory = items.map((item: any) => ({
            ...item,
            category: category, // Add the category from the relationship structure
            relationshipType: 'geo_entities'
          }));
          allRelatedItems.push(...itemsWithCategory);
        }
      });
    }
    
    // Extract from media - respect admin setting - ADD CATEGORY METADATA
    if (Array.isArray(selectedEntity.relationships.media) && config.showMediaInRelatedItemsCloud) {
      console.log('[DataDrawer] Adding media items:', { count: selectedEntity.relationships.media.length });
      // CRITICAL: Add category metadata to each item
      const mediaWithCategory = selectedEntity.relationships.media.map((item: any) => ({
        ...item,
        category: 'media', // Media items have category 'media'
        relationshipType: 'media'
      }));
      allRelatedItems.push(...mediaWithCategory);
    }
    
    console.log('[DataDrawer] Total related items extracted:', {
      totalCount: allRelatedItems.length,
      breakdown: {
        related_entities: selectedEntity.relationships.related_entities ? 
          Object.entries(selectedEntity.relationships.related_entities).map(([cat, items]) => ({
            category: cat,
            count: Array.isArray(items) ? items.length : 0
          })) : [],
        geo_entities: selectedEntity.relationships.geo_entities ? 
          Object.entries(selectedEntity.relationships.geo_entities).map(([cat, items]) => ({
            category: cat,
            count: Array.isArray(items) ? items.length : 0
          })) : [],
        media: Array.isArray(selectedEntity.relationships.media) ? selectedEntity.relationships.media.length : 0
      },
      sampleItem: allRelatedItems[0] ? {
        id: allRelatedItems[0].id,
        name: (allRelatedItems[0] as any).name,
        entity_name: allRelatedItems[0].entity_name,
        category: (allRelatedItems[0] as any).category,
        relationshipType: (allRelatedItems[0] as any).relationshipType
      } : null
    });
    
    return allRelatedItems;
  }, [selectedEntity?.relationships, config.showMediaInRelatedItemsCloud]);

  // Callback to trigger related items mode from DataItemDetails
  const handleShowRelatedItems = useCallback(() => {
    setShowRelatedItems(true);
  }, []);

  // DRY: Utility function for status bar text generation
  const getStatusBarText = useCallback((
    showRelatedItems: boolean,
    selectedEntity: { name?: string; properties?: any } | null,
    relatedItemsData: RelatedEntityItem[],
    filteredCount: number,
    paginationMeta: { count: number; next?: string | null } | null,
    currentEndpoint: { name?: string } | null
  ): string => {
    if (showRelatedItems) {
      // Use adapter pattern to get meaningful name instead of raw entity name
      const meaningfulName = selectedEntity?.properties 
        ? getDataItemName(selectedEntity.properties, currentEndpoint as any)
        : selectedEntity?.name || 'Selected Item';
      return `Showing related items for: ${meaningfulName} (${relatedItemsData.length} items)`;
    }
    
    if (paginationMeta) {
      return `Showing ${filteredCount} of ${paginationMeta.count.toLocaleString()} ${currentEndpoint?.name || 'items'}`;
    }
    
    return `Showing ${filteredCount} ${currentEndpoint?.name || 'items'}`;
  }, []);
  
  // Local state for DataDrawer-specific data
  const [currentEndpoint, setCurrentEndpoint] = useState<ApiEndpoint | null>(null);
  
  
  // Use passed options directly instead of local state
  const options = passedOptions;

  // PERFORMANCE: Track stable entity signature to prevent unnecessary derivations
  const entitySignatureRef = useRef<string>('');
  const lastSelectedItemRef = useRef<any>(null);
  
  // OPTIMIZED: Derive selectedItem with signature-based stability
  const selectedItem = useMemo(() => {
    if (!selectedEntity?.properties) {
      entitySignatureRef.current = '';
      lastSelectedItemRef.current = null;
      return null;
    }
    
    // Create current signature based on actual data content
    const propertiesKeys = Object.keys(selectedEntity.properties).sort();
    const relationshipKeys = selectedEntity.relationships ? Object.keys(selectedEntity.relationships).sort() : [];
    const mediaCount = selectedEntity.relatedMedia?.length || 0;
    const primaryImageId = (selectedEntity.properties as any)?.primary_image?.id || null;
    const currentSignature = `${selectedEntity.id}-${selectedEntity.name}-${selectedEntity.isComplete}-${propertiesKeys.join(',')}-${relationshipKeys.join(',')}-${mediaCount}-${primaryImageId}`;
    
    // Only derive if signature actually changed
    if (entitySignatureRef.current === currentSignature && lastSelectedItemRef.current) {
      return lastSelectedItemRef.current;
    }
    
    // Signature changed - derive new item
    entitySignatureRef.current = currentSignature;
    
    // Include relationships from the top-level selectedEntity, not from properties
    const { relationships: propsRelationships, relatedMedia: propsRelatedMedia, isComplete: propsIsComplete, ...basicProperties } = selectedEntity.properties;
    
    // Create complete item with relationships from selectedEntity
    const completeItem = {
      ...basicProperties,
      // Include important top-level fields from selectedEntity (preserved by SelectionContext)
      thumbnail: selectedEntity.thumbnail,
      primary_image: selectedEntity.primary_image,
      created_at: selectedEntity.created_at,
      last_updated_at: selectedEntity.last_updated_at,
      url: selectedEntity.url, // For media items
      // Include relationships from the top-level selectedEntity (where SelectionContext stores them)
      relationships: selectedEntity.relationships,
      // Include related media for consistency
      relatedMedia: selectedEntity.relatedMedia
    };
    

    
    lastSelectedItemRef.current = completeItem as any;
    return completeItem as any;
  }, [selectedEntity?.id, selectedEntity?.name, selectedEntity?.isComplete, selectedEntity?.thumbnail, selectedEntity?.properties && Object.keys(selectedEntity.properties).length]);

  // Search state management - controlled by DataDrawer and passed to DataGrid
  const [searchTerm, setSearchTerm] = useState(options.initialSearchTerm || '');

  // Get spinner context
  const { loading, startLoading, stopLoading } = useSpinner();
  
  // Use UserInterface context for layout coordination and mode control
  const { 
    openDataDrawerCompact, 
    closeDataDrawer, 
    triggerZoomToHome,
    toggleDataGridCollapse,
    getDataDrawerHeight,
    getDataDrawerTop,
    setUserPreferredView,
    state: { isMediaDrawerVisible, dataDrawerMode, isSidePanelOpen, isDataGridCollapsed, userPreferredView } 
  } = useUserInterface();
  
  // Derive display properties from UserInterfaceContext mode
  const mode = dataDrawerMode;
  
  // Calculate available height for DataItemDetails accounting for MediaDrawer
  const getDataItemDetailsHeight = useMemo(() => {
    const TITLE_BAR_HEIGHT = 66;
    const MEDIA_DRAWER_RESERVED_HEIGHT = 280; // Carousel + toggle space
    
    const hasMedia = selectedEntity?.relatedMedia && selectedEntity.relatedMedia.length > 0;
    if (mode === 'fullscreen' && isMediaDrawerVisible && hasMedia) {
      return `calc(100vh - ${TITLE_BAR_HEIGHT}px - ${MEDIA_DRAWER_RESERVED_HEIGHT}px)`;
    }
    return '100%'; // Full height when no MediaDrawer or in compact mode
  }, [mode, isMediaDrawerVisible, selectedEntity?.relatedMedia]);
  const isOpen = mode !== 'hidden';
  
  // Toggle related items mode with user preference tracking
  const handleToggleRelatedItems = useCallback(() => {
    const newShowRelatedItems = !showRelatedItems;
    
    setShowRelatedItems(newShowRelatedItems);
    
    // Set user preference to prevent auto-switching back
    const userPreference = newShowRelatedItems ? 'related' : 'endpoint';
    setUserPreferredView(userPreference);
  }, [showRelatedItems, setUserPreferredView, userPreferredView]);

  // Smart view switching based on user preference and available data
  useEffect(() => {
    // Auto-switch logic - only when user preference is 'auto'
    
    // Only auto-switch when user preference is 'auto'
    if (userPreferredView === 'auto') {
      if (selectedEntity && selectedEntity.isComplete && relatedItemsData.length > 0 && !showRelatedItems) {
        setShowRelatedItems(true);
      }
      // Reset to main endpoint view when no item is selected
      else if (!selectedEntity && showRelatedItems) {
        setShowRelatedItems(false);
      }
    }
    // Respect manual user preference - prevent auto-switching when user explicitly chose a view
    else if (userPreferredView === 'endpoint' && showRelatedItems) {
      setShowRelatedItems(false);
    }
    else if (userPreferredView === 'related' && !showRelatedItems && relatedItemsData.length > 0) {
      setShowRelatedItems(true);
    }
  }, [selectedEntity?.isComplete, relatedItemsData.length, selectedEntity, userPreferredView]); // Removed showRelatedItems to prevent loops

  // Reset user preference to auto when selection changes (but only when switching to a different entity)
  const [lastSelectedEntityId, setLastSelectedEntityId] = useState<string | number | null>(null);
  useEffect(() => {
    const currentEntityId = selectedEntity?.id || null;
    
    // Only reset preference when switching to a completely different entity
    if (currentEntityId !== lastSelectedEntityId && lastSelectedEntityId !== null) {
      if (userPreferredView !== 'auto') {
        // Reset to auto for new entity selection
        setUserPreferredView('auto');
      }
    }
    
    setLastSelectedEntityId(currentEntityId);
  }, [selectedEntity?.id, userPreferredView, setUserPreferredView]);

  const findEndpoint = (searchTerm: string) => {
    if (!config?.apiEndpoints) {
      return null;
    }
    
    // Try matching by name first (case-insensitive)
    let endpoint = config.apiEndpoints.find(
      ep => ep.name.toLowerCase() === searchTerm.toLowerCase()
    );
    
    // Then try exact match on tableName
    if (!endpoint) {
      endpoint = config.apiEndpoints.find(
        ep => ep.tableName === searchTerm
      );
    }
    
    // Finally try case-insensitive match on tableName
    if (!endpoint) {
      endpoint = config.apiEndpoints.find(
        ep => ep.tableName.toLowerCase() === searchTerm.toLowerCase()
      );
    }
    
    return endpoint;
  };

  // Sync currentEndpoint with selectedEndpoint from SelectionContext
  // This ensures EndpointButtons always reflect the current selection's endpoint
  useEffect(() => {
    // Only sync if we have both a selectedEndpoint AND a selectedEntity
    // This prevents clearing the endpoint when no entity is selected
    if (selectedEndpoint && selectedEntity && selectedEndpoint.tableName !== currentEndpoint?.tableName) {
      setCurrentEndpoint(selectedEndpoint);
    }
  }, [selectedEndpoint, currentEndpoint, selectedEntity]);

  useEffect(() => {
    if (!currentEndpoint && config?.apiEndpoints) {
      // First try to find endpoint from options
      if (options.tableName) {
        const optionsEndpoint = findEndpoint(options.tableName);
        if (optionsEndpoint) {
          setCurrentEndpoint(optionsEndpoint);
          return;
        }
      }

      // Then try to find default endpoint
      const defaultTableName = config.defaultDataTable || "tags";
      const defaultEndpoint = findEndpoint(defaultTableName);
      if (defaultEndpoint) {
        setCurrentEndpoint(defaultEndpoint);
        return;
      }

      // Fallback to first endpoint
      setCurrentEndpoint(config.apiEndpoints[0] || null);
    }
  }, [currentEndpoint, config?.apiEndpoints, options]);



  // Track fetch attempts to prevent repeated API calls
  const [hasAttemptedFetch, setHasAttemptedFetch] = useState(false);

  const { enqueueSnackbar } = useSnackbar();
  const { profile } = useUser();

  // Effect: Handle map clicks - only show in DataDrawer if it's already open
  // If DataDrawer is closed, map clicks should show in popup instead
  useEffect(() => {
    // Only respond to map clicks if DataDrawer is already open
    if (selectedEntity?.properties?.fromMapClick && mode !== 'hidden') {
      console.log('[DataDrawer] Map click detected with DataDrawer open - showing in DataDrawer');
      // DataDrawer is already open, so the selectedEntity will be shown in DataItemDetails
      // No need to do anything special here
    }
  }, [selectedEntity, mode]);

  // Note: Popup closure when DataDrawer opens is now handled centrally in UserInterfaceContext

  // Generic data fetching function using adapter pattern
  const fetchData = useCallback(async (isLoadMore = false) => {
    if (!currentEndpoint?.tableName || loading || (isLoadMore && isLoadingMore)) return;
    
    try {
      if (isLoadMore) {
        setIsLoadingMore(true);
      } else {
        startLoading();
      }
      
      // Use generic adapter function instead of data source-specific logic
      const result = await fetchEndpointData(currentEndpoint!, profile, {
        isLoadMore,
        paginationUrl: isLoadMore ? paginationMeta?.next || undefined : undefined,
        filters: currentEndpoint!.tableName === 'media' ? activeFilters : {},
        fields: 'id,name,description,thumbnail'
      });
      
      if (!isLoadMore) {
        setHasAttemptedFetch(true);
      }
      
      if (isLoadMore) {
        // Append new results to existing data, but filter out any duplicates based on ID
        setData(prevData => {
          const existingIds = new Set(prevData.map(item => item.id));
          const newItems = result.data.filter(item => !existingIds.has(item.id));
          const totalAfterMerge = prevData.length + newItems.length;
          console.log(`[DataDrawer] Loaded ${result.data.length} items from adapter, ${newItems.length} new items added. Total: ${totalAfterMerge}`);
          return [...prevData, ...newItems];
        });
      } else {
        // Replace data (initial load or refresh)
        setData(result.data);
      }
      
      setPaginationMeta(result.pagination);
      
      if (!isLoadMore && result.data.length === 0) {
        const totalCount = result.pagination?.count;
        const countMessage = totalCount !== undefined ? ` (${totalCount} total)` : '';
        enqueueUniqueSnackbar(
          enqueueSnackbar,
          `No records found for ${currentEndpoint!.name}${isMediaEndpoint && Object.keys(activeFilters).some(k => activeFilters[k]) ? ' with current filters' : ''}${countMessage}`,
          { variant: 'info' }
        );
      }
    } catch (error) {
      if (!isLoadMore) {
        setData([]);
      }
        enqueueUniqueSnackbar(
          enqueueSnackbar,
          `Error loading ${isLoadMore ? 'more ' : ''}${currentEndpoint!.name}`,
          { variant: 'error' }
        );
      if (!isLoadMore) {
        setHasAttemptedFetch(true);
      }
    } finally {
      if (isLoadMore) {
        setIsLoadingMore(false);
      } else {
        stopLoading();
      }
    }
  }, [currentEndpoint, loading, isLoadingMore, startLoading, stopLoading, enqueueSnackbar, activeFilters, isMediaEndpoint, paginationMeta]);

  // Load more function for infinite scroll
  const handleLoadMore = useCallback(() => {
    if (paginationMeta?.next && !isLoadingMore && !loading) {
      console.log('[DataDrawer] Loading more data via infinite scroll');
      fetchData(true);
    }
  }, [fetchData, paginationMeta?.next, isLoadingMore, loading]);

  // Generic server search function using adapter pattern
  const handleServerSearch = useCallback(async (searchTerm: string) => {
    if (!currentEndpoint?.tableName || serverSearchLoading) return;
    
    console.log('[DataDrawer] Server search requested for:', searchTerm);
    setServerSearchLoading(true);
    
    try {
      // Use generic adapter function instead of data source-specific logic
      const result = await searchEndpoint(currentEndpoint!, searchTerm, profile, {
        filters: currentEndpoint!.tableName === 'media' ? activeFilters : {},
        fields: 'id,name,description,thumbnail'
      });
      
      // Replace current data with search results
      setData(result.data);
      setPaginationMeta(result.pagination);
      setHasAttemptedFetch(true);
      
      const resultText = result.data.length === 1 ? 'result' : 'results';
      enqueueUniqueSnackbar(
        enqueueSnackbar,
        `Found ${result.data.length} ${resultText} for "${searchTerm}" in ${currentEndpoint!.name}`,
        { variant: 'success' }
      );
      
    } catch (error) {
      console.error('[DataDrawer] Server search error:', error);
      enqueueUniqueSnackbar(
        enqueueSnackbar,
        `Error searching ${currentEndpoint!.name}: ${error instanceof Error ? error.message : 'Unknown error'}`,
        { variant: 'error' }
      );
    } finally {
      setServerSearchLoading(false);
    }
  }, [currentEndpoint, serverSearchLoading, activeFilters, enqueueSnackbar]);

  // Auto-search hook for handling initial search terms (e.g., from home page cards)
  // Placed here after handleServerSearch is defined
  useAutoSearch({
    initialSearchTerm: options.initialSearchTerm,
    currentEndpoint: currentEndpoint,
    onServerSearch: handleServerSearch,
    setSearchTerm
  });

  useEffect(() => {
    setIsMediaEndpoint(currentEndpoint?.tableName === 'media');
    
    // Reset filters when changing away from media
    if (currentEndpoint?.tableName !== 'media') {
      setActiveFilters({});
    }
  }, [currentEndpoint]);

  const handleFilterChange = (key: string, value: string, clearAll: boolean = false) => {
    if (clearAll) {
      setActiveFilters({});
    } else {
      setActiveFilters(prev => ({
        ...prev,
        [key]: value
      }));
    }
    
    // Reset data and trigger a new fetch
    setData([]);
    setHasAttemptedFetch(false);
    setIsLoadingMore(false);
    setServerSearchLoading(false);
    setPaginationMeta(null);
  };

  const handleBulkEdit = () => {
    if (data.length > 0 && onOpenBulkEdit) {
      const itemIds = data.map(item => String(item.id));
      onOpenBulkEdit(itemIds);
    } else {
      enqueueUniqueSnackbar(
        enqueueSnackbar,
        "No items available to edit",
        { variant: 'warning' }
      );
    }
  };

  // MINIMAL FIX: Track previous endpoint to prevent unnecessary data clearing
  const previousEndpointRef = useRef<string | null>(null);
  
  useEffect(() => {
    const currentTableName = currentEndpoint?.tableName || null;
    
    // Only clear data when endpoint actually changes, not when it becomes temporarily null
    if (currentTableName && currentTableName !== previousEndpointRef.current) {
      console.log('[DataDrawer] Endpoint changed - clearing data:', {
        previousEndpoint: previousEndpointRef.current,
        newEndpoint: currentTableName,
        reason: 'Legitimate endpoint switch'
      });
      
      setData([]);
      setHasAttemptedFetch(false);
      setIsLoadingMore(false);
      setServerSearchLoading(false);
      setPaginationMeta(null);
      setFilteredCount(0);
      setHiddenDueToImages(0);
      
      previousEndpointRef.current = currentTableName;
    } else if (currentTableName && !previousEndpointRef.current) {
      // Initialize the ref on first valid endpoint
      previousEndpointRef.current = currentTableName;
    }
    // Note: When currentTableName is null, we preserve data and don't update the ref
    // This prevents data clearing during temporary context cleanup
  }, [currentEndpoint?.tableName]);

  useEffect(() => {
    if (currentEndpoint?.tableName && data.length === 0 && !loading && !hasAttemptedFetch) {
      fetchData();
    }
  }, [currentEndpoint?.tableName, data.length, loading, hasAttemptedFetch, fetchData]);

  const handleSelectItem = async (item: any) => {
    if (!currentEndpoint || !config?.apiEndpoints) return;
    
    console.log('[DataDrawer] Item selected, determining correct endpoint:', {
      itemId: item.id,
      itemName: item.name,
      assumedEndpoint: currentEndpoint.name,
      dataSourceType: currentEndpoint.dataSourceType,
      hasCorrectEndpoint: !!(item.properties?.correctEndpoint),
      correctEndpointName: item.properties?.correctEndpoint?.name,
      itemEntityType: item.entityType,
      itemProperties: item.properties ? Object.keys(item.properties) : null,
      relatedCategory: item.properties?.relatedCategory,
      relatedEndpointName: item.properties?.relatedEndpoint?.name
    });
    
    let correctEndpoint = currentEndpoint;
    
    // PRIORITY 1: Use pre-determined correct endpoint from related items processing
    if (item.properties?.correctEndpoint) {
      correctEndpoint = item.properties.correctEndpoint;
      console.log('[DataDrawer] Using pre-determined correct endpoint for related item:', {
        itemId: item.id,
        correctEndpointName: correctEndpoint.name,
        correctTableName: correctEndpoint.tableName
      });
    }
    // PRIORITY 1.5: Fallback to relatedEndpoint if correctEndpoint not set
    else if (item.properties?.relatedEndpoint) {
      correctEndpoint = item.properties.relatedEndpoint;
      console.log('[DataDrawer] Using related endpoint as fallback for related item:', {
        itemId: item.id,
        relatedEndpointName: correctEndpoint.name,
        relatedTableName: correctEndpoint.tableName
      });
    }
    // PRIORITY 2: Enhanced endpoint detection for all items
    else {
      // Check if the item's entityType matches the effective endpoint
      if (item.entityType && item.entityType !== currentEndpoint.tableName) {
        // Find the correct endpoint based on the item's entityType
        const entityBasedEndpoint = config.apiEndpoints.find(ep => 
          ep.tableName === item.entityType ||
          ep.name.toLowerCase() === item.entityType.toLowerCase()
        );
        
        if (entityBasedEndpoint) {
          correctEndpoint = entityBasedEndpoint;
          console.log('[DataDrawer] Using entityType-based endpoint for item:', {
            itemId: item.id,
            itemEntityType: item.entityType,
            currentEndpointTable: currentEndpoint.tableName,
            correctEndpointName: correctEndpoint.name,
            correctEndpointTable: correctEndpoint.tableName
          });
        }
      }
      
      // Fallback to traditional endpoint detection if needed
      if (correctEndpoint === currentEndpoint && currentEndpoint.api_endpoint) {
        correctEndpoint = await findCorrectEndpointForItem(
          item.id,
          item.name,
          config.apiEndpoints,
          currentEndpoint
        );
      } else if (!currentEndpoint.api_endpoint) {
        console.log('[DataDrawer] Skipping endpoint detection for non-API endpoint');
      }
    }
    
    // Simply set the entity in SelectionContext - let DataItemDetails handle the data fetching
    setSelectedEntity({
      id: item.id,
      name: item.name,
      entityType: correctEndpoint.tableName,
      layerId: correctEndpoint.mapLayerId || correctEndpoint.tableName,
      properties: {
        ...item,
        fromDataDrawer: true // Mark as coming from DataDrawer to prevent loops
      },
      isComplete: false // Let DataItemDetails fetch complete data
    }, correctEndpoint); // Pass the correct endpoint to SelectionContext
  };

  const handleEndpointChange = useCallback((endpoint: ApiEndpoint) => {
    console.log('[DataDrawer] Manual endpoint change via EndpointButtons:', {
      newEndpointName: endpoint.name,
      newEndpointTableName: endpoint.tableName,
      previousEndpointName: currentEndpoint?.name,
      hasSelectedEntity: !!selectedEntity,
      currentSearchTerm: searchTerm
    });
    
    setCurrentEndpoint(endpoint);
    
    // Clear the search term when switching endpoints since it's specific to the previous endpoint's data
    if (searchTerm.trim()) {
      console.log('[DataDrawer] Clearing search term due to endpoint change:', searchTerm);
      setSearchTerm('');
    }
    
    // When manually changing endpoints, clear any existing selection
    // and update SelectionContext to reflect the new browsing context
    if (selectedEntity) {
      console.log('[DataDrawer] Clearing selection due to manual endpoint change');
      setSelectedEntity(null, endpoint);
    } else {
      // If no entity is selected, just update the endpoint in SelectionContext
      // This keeps the EndpointButtons in sync for future selections
      console.log('[DataDrawer] Updating SelectionContext endpoint for browsing mode');
      setSelectedEntity(null, endpoint);
    }
  }, [setSelectedEntity, currentEndpoint, selectedEntity, searchTerm, setSearchTerm]);

  // PERFORMANCE: Memoize handleItemUpdate to prevent DataItemDetails re-renders
  const handleItemUpdate = useCallback(async (updatedItem: any) => {
    // Update the data array with the new item data
    setData((prevData) => prevData.map((item) => 
      item.id === updatedItem.id ? updatedItem : item
    ));
    
    // Update the SelectionContext with the new data instead of clearing it
    // This keeps the user in the detail view after edits
    if (selectedEntity && updatedItem.id === selectedEntity.properties?.id) {
      // Create updated entity with new properties
      const updatedEntity = {
        ...selectedEntity,
        properties: updatedItem,
        isComplete: true
      };
      setSelectedEntity(updatedEntity);
    }

    // Refresh the corresponding map layer to show the updated item
    if (currentEndpoint && layers && currentEndpoint.mapLayerId) {
      const matchingLayer = layers.find(layer => layer.id === currentEndpoint.mapLayerId);
      
      if (matchingLayer) {
        console.log(`[DataDrawer] Refreshing map layer '${matchingLayer.id}' for updated ${currentEndpoint.name} item`);
        refreshLayer(matchingLayer.id);
      }
    }
  }, [setData, selectedEntity, setSelectedEntity, currentEndpoint, layers, refreshLayer]);

  // PERFORMANCE: Memoize handleItemDelete to prevent DataItemDetails re-renders
  const handleItemDelete = useCallback((deletedItemId: number | string) => {
    // Remove the deleted item from the data array  
    setData((prevData) => prevData.filter((item) => item.id !== deletedItemId));
    // Clear the SelectionContext since the item was deleted
    setSelectedEntity(null);
    // Also switch off related items mode to show endpoint items
    setShowRelatedItems(false);

    // Refresh the corresponding map layer to remove the deleted item
    if (currentEndpoint && layers && currentEndpoint.mapLayerId) {
      const matchingLayer = layers.find(layer => layer.id === currentEndpoint.mapLayerId);
      
      if (matchingLayer) {
        console.log(`[DataDrawer] Refreshing map layer '${matchingLayer.id}' for deleted ${currentEndpoint.name} item`);
        refreshLayer(matchingLayer.id);
      }
    }

    // Zoom to home view since the selection is cleared
    console.log(`[DataDrawer] Triggering zoom to home after deleting ${currentEndpoint?.name} item`);
    triggerZoomToHome();
  }, [setData, setSelectedEntity, currentEndpoint, layers, refreshLayer, triggerZoomToHome]);

  // PERFORMANCE: Memoize onBack to prevent DataItemDetails re-renders
  const handleBack = useCallback(() => {
    // Only clear selection if user explicitly wants to go back to grid
    // Don't auto-clear after edits - let user decide
    setSelectedEntity(null);
    // Also switch off related items mode to show endpoint items
    setShowRelatedItems(false);
  }, [setSelectedEntity]);

  const activeFilterCount = useMemo(() => {
    return Object.values(activeFilters).filter(value => value !== '').length;
  }, [activeFilters]);

  const toggleFilterToolbar = () => {
    setIsFilterToolbarOpen(!isFilterToolbarOpen);
  };

  const handleAddNew = () => {
    setIsCreateDialogOpen(true);
  };

  const handleFilteredCountChange = useCallback((filteredCount: number, hiddenDueToImages: number) => {
    setFilteredCount(filteredCount);
    setHiddenDueToImages(hiddenDueToImages);
  }, []);

  const handleItemCreated = (newItem: any) => {
    // Add the new item to the beginning of the data array
    setData((prevData) => [newItem, ...prevData]);
    
    // Update pagination meta if it exists
    if (paginationMeta) {
      setPaginationMeta((prevMeta) => prevMeta ? {
        ...prevMeta,
        count: prevMeta.count + 1
      } : null);
    }

    // Refresh the corresponding map layer to show the new item
    if (currentEndpoint && layers && currentEndpoint.mapLayerId) {
      // Find the matching layer using the mapLayerId from the endpoint config
      const matchingLayer = layers.find(layer => layer.id === currentEndpoint.mapLayerId);
      
      if (matchingLayer) {
        console.log(`[DataDrawer] Refreshing map layer '${matchingLayer.id}' for new ${currentEndpoint.name} item`);
        refreshLayer(matchingLayer.id);
      } else {
        console.log(`[DataDrawer] No matching map layer found for endpoint '${currentEndpoint.name}' with mapLayerId '${currentEndpoint.mapLayerId}'`);
      }
    }
  };

  // Function to show only the active table's layer on the map
  const showOnlyActiveTableOnMap = useCallback(() => {
    if (!currentEndpoint || !layers) return;

    const targetLayerId = currentEndpoint.mapLayerId;
    if (!targetLayerId) {
      enqueueUniqueSnackbar(
        enqueueSnackbar,
        `No map layer associated with ${currentEndpoint.name}`,
        { variant: 'warning' }
      );
      return;
    }

    // Check if the target layer exists
    const targetLayer = layers.find(layer => layer.id === targetLayerId);
    if (!targetLayer) {
      enqueueUniqueSnackbar(
        enqueueSnackbar,
        `Map layer '${targetLayerId}' not found`,
        { variant: 'warning' }
      );
      return;
    }

    // Hide all layers first
    layers.forEach(layer => {
      setLayerVisibility(layer.id, false);
    });

    // Show only the target layer
    setLayerVisibility(targetLayerId, true);

    enqueueUniqueSnackbar(
      enqueueSnackbar,
      `Now showing only ${currentEndpoint.name} on the map`,
      { variant: 'success' }
    );
  }, [currentEndpoint, layers, setLayerVisibility, enqueueSnackbar]);

  // Determine display properties based on mode
  const isCompactMode = mode === 'compact';
  const isFullscreenMode = mode === 'fullscreen';
  const isHidden = mode === 'hidden';
  
  // Calculate width based on mode
  const drawerWidth = isFullscreenMode ? '100vw' : '100%'; // Use 100% of Grid column
  
  // Restore proper height calculation that coordinates with CalendarView
  const drawerHeight = useMemo(() => {
    const height = getDataDrawerHeight(currentEndpoint);
    if (process.env.NODE_ENV === 'development') {
      console.log('[DataDrawer] Height calculated:', { height, mode: mode, endpoint: currentEndpoint?.name });
    }
    return height;
  }, [getDataDrawerHeight, mode, currentEndpoint]);
  
  const drawerTop = useMemo(() => {
    const top = getDataDrawerTop(currentEndpoint);
    if (process.env.NODE_ENV === 'development') {
      console.log('[DataDrawer] Top position calculated:', { top, mode: mode, endpoint: currentEndpoint?.name });
    }
    return top;
  }, [getDataDrawerTop, mode, currentEndpoint]);
  
  // Calculate background with radial vignette for compact mode
  const getBackgroundStyle = () => {
    if (isFullscreenMode) {
      // Reduce opacity in Tables mode to show more of the map underneath
      const baseOpacity = config.backgroundOpacity || 0.4;
      const tablesOpacity = baseOpacity * 0.6; // 60% of original opacity for Tables mode
      const opacity = Math.round(tablesOpacity * 255).toString(16).padStart(2, '0');
      return `${theme.palette.secondary.main}${opacity}`; // Use theme secondary color with reduced opacity
    }
    return isCompactMode 
      ? "radial-gradient(circle at center, rgba(30, 30, 30, 0.85) 0%, rgba(20, 20, 20, 0.90) 40%, rgba(10, 10, 10, 0.95) 80%, rgba(0, 0, 0, 0.98) 100%)" // Central vignette burst
      : "transparent";
  };
  
  // Get the backdrop style for fullscreen mode
  const getBackdropStyle = () => {
    if (isFullscreenMode) {
      // Reduce opacity in Tables mode to show more of the map underneath
      const baseOpacity = config.backgroundOpacity || 0.4;
      const tablesOpacity = baseOpacity * 0.6; // 60% of original opacity for Tables mode
      const opacity = Math.round(tablesOpacity * 255).toString(16).padStart(2, '0');
      return {
        style: {
          backgroundColor: `${theme.palette.secondary.main}${opacity}`, // Use theme secondary color with reduced opacity
        }
      };
    }
    return {
      style: {
        backgroundColor: "transparent",
      }
    };
  };

  // Always render the Drawer to allow proper open/close animations
  return (
    <>
      <Drawer
        anchor="right"
        open={isOpen}
        onClose={() => closeDataDrawer()}
        variant="persistent"
        BackdropProps={getBackdropStyle()}
        PaperProps={{
          style: {
            position: isFullscreenMode ? "fixed" : "absolute",
            top: isFullscreenMode ? drawerTop : "0", // Use reactive top position
            left: isFullscreenMode ? "0" : "auto",
            background: getBackgroundStyle(),
            backdropFilter: isCompactMode ? "blur(10px)" : "none", // No blur in Tables mode for clear map visibility
            border: `1px solid ${theme.palette.mode === 'dark' 
              ? 'rgba(255, 255, 255, 0.1)' 
              : 'rgba(255, 255, 255, 0.2)'}`,
            boxShadow: theme.palette.mode === 'dark'
              ? '0 8px 32px 0 rgba(0, 0, 0, 0.37)'
              : '0 8px 32px 0 rgba(31, 38, 135, 0.37)',
            transition: "all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)", // Smooth eased transition
            width: drawerWidth,
            height: drawerHeight, // Use calculated height that accounts for CalendarView
            zIndex: 1100,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            pointerEvents: isCompactMode ? "auto" : "none",
          },
        }}
      >
        {/* Only render content when drawer is open */}
        {isOpen && (
          <>
            <Box
              sx={{
                width: "100%",
                height: "auto",
                bgcolor: "black",
                py: 2,
                px: 2,
                display: "flex",
                flexDirection: "column",
                gap: 2,
                pointerEvents: "auto",
              }}
            >
              <EndpointButtons
                endpoints={config.apiEndpoints}
                currentEndpoint={currentEndpoint}
                onEndpointChange={handleEndpointChange}
                disabled={!!selectedItem}
                isLoading={loading}
              />

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Box sx={{ display: "flex", gap: 1 }}>
                  {selectedItem && (
                    <CMButton
                      onClick={() => {
                        // Clear the SelectionContext when explicitly going back to grid
                        setSelectedEntity(null);
                        // Also switch off related items mode to show endpoint items
                        setShowRelatedItems(false);
                      }}
                      variant="outlined"
                      size={isFullscreenMode ? "medium" : "small"}
                    >
                      Back to Grid
                    </CMButton>
                  )}

                  {/* Map Layer Controls */}
                  {!selectedItem && currentEndpoint && currentEndpoint.mapLayerId && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Tooltip title={`Only show ${currentEndpoint.name} on the map`}>
                        <IconButton
                          onClick={showOnlyActiveTableOnMap}
                          size="small"
                          sx={{
                            color: 'rgba(255, 255, 255, 0.9)', // Bright white for visibility
                            backgroundColor: 'rgba(255, 255, 255, 0.1)', // Subtle background
                            border: '1px solid rgba(255, 255, 255, 0.2)', // Subtle border
                            '&:hover': {
                              color: '#ffffff',
                              backgroundColor: 'rgba(255, 255, 255, 0.2)',
                              transform: 'scale(1.05)',
                            },
                            transition: 'all 0.2s ease-in-out'
                          }}
                        >
                          <VisibilityIcon />
                        </IconButton>
                      </Tooltip>
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          color: 'rgba(255, 255, 255, 0.8)', 
                          cursor: 'pointer',
                          fontSize: '0.7rem',
                          fontWeight: 500,
                          '&:hover': {
                            color: 'rgba(255, 255, 255, 1)',
                          }
                        }}
                        onClick={showOnlyActiveTableOnMap}
                      >
                        Only Show {currentEndpoint.name} On Map
                      </Typography>
                    </Box>
                  )}

                  {isMediaEndpoint && !selectedItem && !isFilterToolbarOpen && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Tooltip title="Show Filters">
                        <IconButton 
                          onClick={toggleFilterToolbar}
                          color="primary"
                          size="small"
                        >
                          <Badge badgeContent={activeFilterCount} color="secondary">
                            <FilterListIcon />
                          </Badge>
                        </IconButton>
                      </Tooltip>
                      <Typography 
                        variant="caption" 
                        sx={{ color: 'grey.400', cursor: 'pointer' }}
                        onClick={toggleFilterToolbar}
                      >
                        Show Filters
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>

            {isMediaEndpoint && !selectedItem && isFilterToolbarOpen && (
              <FilterToolbar
                config={config}
                activeFilters={activeFilters}
                onFilterChange={handleFilterChange}
                onBulkEdit={onOpenBulkEdit ? handleBulkEdit : undefined}
                onHide={toggleFilterToolbar}
              />
            )}

            {loading && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: 'rgba(0, 0, 0, 0)',
                  zIndex: 1200,
                }}
              >
                <CircularProgress size={60} color="secondary" />
              </Box>
            )}

            {isFullscreenMode ? (
              // Dynamic layout for Tables tab (fullscreen mode)
              selectedItem ? (
                // Split layout when item is selected
                <Box 
                  sx={{ 
                    display: 'flex', 
                    flexDirection: 'column',
                    height: '100%', 
                    pointerEvents: 'auto'
                  }}
                >
                  {/* Top Section: Split DataGrid and DataItemDetails */}
                  <Box 
                    sx={{ 
                      display: 'flex', 
                      height: (() => {
                        const hasMedia = (selectedEntity?.relatedMedia?.length ?? 0) > 0;
                        if (!hasMedia || !isMediaDrawerVisible) return '100%'; // Full height if no media or MediaDrawer closed
                        return '70%'; // Reserve space for MediaDrawer when open
                      })(),
                      gap: 0.5,
                      position: 'relative',
                      transition: 'height 0.3s ease-in-out' // Smooth height transitions
                    }}
                  >
                    {/* Left Panel: DataGrid (Collapsible) */}
                    {!isDataGridCollapsed && (
                      <Box 
                        sx={{ 
                          width: '50%', // Fixed 50% since DataItemDetails is always visible
                          display: 'flex', 
                          flexDirection: 'column',
                          overflow: 'hidden',
                          borderRight: `1px solid ${theme.palette.divider}`,
                          transition: 'width 0.3s ease-in-out',
                          position: 'relative'
                        }}
                      >
                        <Box sx={{ flex: 1, overflow: "hidden" }}>
                          <DataGrid
                            data={data}
                            onSelect={handleSelectItem}
                            compact={true} // Use compact mode in split layout
                            currentEndpoint={currentEndpoint}
                            searchTerm={searchTerm}
                            setSearchTerm={setSearchTerm}
                            onLoadMore={handleLoadMore}
                            hasMoreData={!!paginationMeta?.next}
                            isLoadingMore={isLoadingMore}
                            onServerSearch={handleServerSearch}
                            serverSearchLoading={serverSearchLoading}
                            onRefresh={() => fetchData(false)}
                            refreshDisabled={loading}
                            onAdd={handleAddNew}
                            showAddNew={true}
                            onFilteredCountChange={handleFilteredCountChange}
                            viewMode={viewMode}
                            onViewModeChange={setViewMode}
                            showRelatedItems={showRelatedItems}
                            relatedItemsData={relatedItemsData}
                            onToggleRelatedItems={handleToggleRelatedItems}
                            selectedEntityName={selectedEntity?.name}
                            onStatusText={() => getStatusBarText(showRelatedItems, selectedEntity, relatedItemsData, filteredCount, paginationMeta, currentEndpoint)}
                          />
                        </Box>
                        
                        {/* DataGrid Collapse Toggle */}
                        <Box
                          sx={{
                            position: 'absolute',
                            top: '50%',
                            left: 10, // Inside the panel, away from screen edge
                            transform: 'translateY(-50%)',
                            zIndex: UI_CONSTANTS.Z_INDEX.TOGGLE_BUTTON + 100,
                            backgroundColor: 'rgba(0, 0, 0, 0.9)',
                            borderRadius: '50%',
                            padding: '4px',
                            border: '2px solid rgba(255, 255, 255, 0.3)',
                            '&:hover': {
                              backgroundColor: 'rgba(0, 0, 0, 1)',
                              transform: 'translateY(-50%) scale(1.1)',
                            },
                            transition: 'all 0.2s ease-in-out'
                          }}
                        >
                          <ToggleButton
                            isOpen={!isDataGridCollapsed}
                            onClick={toggleDataGridCollapse}
                            direction="horizontal"
                            showIndicator={false}
                          />
                        </Box>
                      </Box>
                    )}
                    
                    {/* Right Panel: DataItemDetails (Always visible) */}
                    <Box 
                      sx={{ 
                        width: isDataGridCollapsed ? '100%' : '50%', 
                        overflow: 'hidden',
                        transition: 'width 0.3s ease-in-out',
                        position: 'relative'
                      }}
                    >
                        <ErrorBoundary>
                        <DataItemDetails
                          item={selectedItem}
                          onBack={handleBack}
                          dataDrawerEndpoint={currentEndpoint}
                          dataItemEndpoint={currentEndpoint}
                          onItemUpdate={handleItemUpdate}
                          onItemDelete={handleItemDelete}
                          onOpenBulkEdit={onOpenBulkEdit}
                          isEditingDisabled={!isEndpointEditable(currentEndpoint || {})}
                          availableHeight="100%"
                          onShowRelatedItems={handleShowRelatedItems}
                          hideRelatedItemsRing={isFullscreenMode} // Hide ring in Tables view (fullscreen mode)
                          key={`item-${selectedItem?.id}-${selectedEntity?.isComplete ? 'complete' : 'incomplete'}`}
                        />
                        </ErrorBoundary>
                    </Box>
                    
                    {/* Restore DataGrid button when collapsed */}
                    {isDataGridCollapsed && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: '50%',
                          left: 20, // Away from screen edge, inside the container
                          transform: 'translateY(-50%)',
                          zIndex: UI_CONSTANTS.Z_INDEX.TOGGLE_BUTTON + 100,
                          backgroundColor: 'rgba(0, 0, 0, 0.9)',
                          borderRadius: '50%',
                          padding: '4px',
                          border: '2px solid rgba(255, 255, 255, 0.3)',
                          '&:hover': {
                            backgroundColor: 'rgba(0, 0, 0, 1)',
                            transform: 'translateY(-50%) scale(1.1)',
                          },
                          transition: 'all 0.2s ease-in-out'
                        }}
                      >
                        <ToggleButton
                          isOpen={false}
                          onClick={toggleDataGridCollapse}
                          direction="horizontal"
                          showIndicator={false}
                        />
                      </Box>
                    )}
                  </Box>
                  
                  {/* MediaDrawer toggle is handled by MediaDrawer component itself */}
                </Box>
              ) : (
                // Fullscreen DataGrid when no item selected
                <Box sx={{ pointerEvents: "auto", height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <Box sx={{ flex: 1, overflow: "hidden" }}>
                    <DataGrid
                      data={data}
                      onSelect={handleSelectItem}
                      compact={false}
                      currentEndpoint={currentEndpoint}
                      searchTerm={searchTerm}
                      setSearchTerm={setSearchTerm}
                      onLoadMore={handleLoadMore}
                      hasMoreData={!!paginationMeta?.next}
                      isLoadingMore={isLoadingMore}
                      onServerSearch={handleServerSearch}
                      serverSearchLoading={serverSearchLoading}
                      onRefresh={() => fetchData(false)}
                      refreshDisabled={loading}
                      onAdd={handleAddNew}
                      showAddNew={true}
                      onFilteredCountChange={handleFilteredCountChange}
                      viewMode={viewMode}
                      onViewModeChange={setViewMode}
                      showRelatedItems={showRelatedItems}
                      relatedItemsData={relatedItemsData}
                      onToggleRelatedItems={handleToggleRelatedItems}
                      selectedEntityName={selectedEntity?.name}
                      onStatusText={() => getStatusBarText(showRelatedItems, selectedEntity, relatedItemsData, filteredCount, paginationMeta, currentEndpoint)}
                    />
                  </Box>
                  {hasAttemptedFetch && (
                    <Box
                      sx={{
                        pointerEvents: "auto",
                        bgcolor: theme.palette.grey[900],
                        borderTop: `1px solid ${theme.palette.divider}`,
                        px: 2,
                        py: 1.5,
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        minHeight: "48px",
                      }}
                    >
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          color: theme.palette.common.white,
                          fontWeight: 500 
                        }}
                      >
                        {paginationMeta ? (
                          <>
                            {getStatusBarText(showRelatedItems, selectedEntity, relatedItemsData, filteredCount, paginationMeta, currentEndpoint)}
                            {!showRelatedItems && hiddenDueToImages > 0 && (
                              <span style={{ color: theme.palette.warning.main, fontWeight: 'normal' }}>
                                {` • ${hiddenDueToImages} hidden (no images)`}
                              </span>
                            )}
                            {paginationMeta.next && (
                              <span style={{ color: theme.palette.grey[400], fontWeight: 'normal' }}>
                                {' • Scroll for more'}
                              </span>
                            )}
                          </>
                        ) : (
                          <>
                            {getStatusBarText(showRelatedItems, selectedEntity, relatedItemsData, filteredCount, null, currentEndpoint)}
                            {!showRelatedItems && hiddenDueToImages > 0 && (
                              <span style={{ color: theme.palette.warning.main, fontWeight: 'normal' }}>
                                {` • ${hiddenDueToImages} hidden (no images)`}
                              </span>
                            )}
                          </>
                        )}
                      </Typography>
                      
                      {isLoadingMore && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CircularProgress size={16} color="primary" />
                          <Typography 
                            variant="body2" 
                            sx={{ color: theme.palette.grey[400] }}
                          >
                            Loading...
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  )}
                </Box>
              )
            ) : (
              // Original behavior for compact mode
              selectedItem ? (
                <Box sx={{ pointerEvents: "auto", flex: 1, overflow: "hidden" }}>
                  <ErrorBoundary>
                    <DataItemDetails
                      item={selectedItem}
                      onBack={handleBack}
                      dataDrawerEndpoint={currentEndpoint}
                      dataItemEndpoint={currentEndpoint}
                      onItemUpdate={handleItemUpdate}
                      onItemDelete={handleItemDelete}
                      onOpenBulkEdit={onOpenBulkEdit}
                      isEditingDisabled={!isEndpointEditable(currentEndpoint || {})}
                      availableHeight={getDataItemDetailsHeight}
                      onShowRelatedItems={handleShowRelatedItems}
                      hideRelatedItemsRing={false} // Show ring in compact mode (Map view)
                      key={`item-${selectedItem?.id}-${selectedEntity?.isComplete ? 'complete' : 'incomplete'}`}
                    />
                  </ErrorBoundary>
                </Box>
              ) : (
                <>
                  <Box sx={{ pointerEvents: "auto", flex: 1, overflow: "hidden" }}>
                    <DataGrid
                      data={data}
                      onSelect={handleSelectItem}
                      compact={isCompactMode}
                      currentEndpoint={currentEndpoint}
                      searchTerm={searchTerm}
                      setSearchTerm={setSearchTerm}
                      onLoadMore={handleLoadMore}
                      hasMoreData={!!paginationMeta?.next}
                      isLoadingMore={isLoadingMore}
                      onServerSearch={handleServerSearch}
                      serverSearchLoading={serverSearchLoading}
                      onRefresh={() => fetchData(false)}
                      refreshDisabled={loading}
                      onAdd={handleAddNew}
                      showAddNew={true}
                      onFilteredCountChange={handleFilteredCountChange}
                      viewMode={viewMode}
                      onViewModeChange={setViewMode}
                      showRelatedItems={showRelatedItems}
                      relatedItemsData={relatedItemsData}
                      onToggleRelatedItems={handleToggleRelatedItems}
                      selectedEntityName={selectedEntity?.name}
                      onStatusText={() => getStatusBarText(showRelatedItems, selectedEntity, relatedItemsData, filteredCount, paginationMeta, currentEndpoint)}
                    />
                  </Box>
                  {hasAttemptedFetch && (
                    <Box
                      sx={{
                        pointerEvents: "auto",
                        bgcolor: theme.palette.grey[900],
                        borderTop: `1px solid ${theme.palette.divider}`,
                        px: 2,
                        py: 1.5,
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        minHeight: "48px",
                      }}
                    >
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          color: theme.palette.common.white,
                          fontWeight: 500 
                        }}
                      >
                        {paginationMeta ? (
                          <>
                            {getStatusBarText(showRelatedItems, selectedEntity, relatedItemsData, filteredCount, paginationMeta, currentEndpoint)}
                            {!showRelatedItems && hiddenDueToImages > 0 && (
                              <span style={{ color: theme.palette.warning.main, fontWeight: 'normal' }}>
                                {` • ${hiddenDueToImages} hidden (no images)`}
                              </span>
                            )}
                            {paginationMeta.next && (
                              <span style={{ color: theme.palette.grey[400], fontWeight: 'normal' }}>
                                {' • Scroll for more'}
                              </span>
                            )}
                          </>
                        ) : (
                          <>
                            {getStatusBarText(showRelatedItems, selectedEntity, relatedItemsData, filteredCount, null, currentEndpoint)}
                            {!showRelatedItems && hiddenDueToImages > 0 && (
                              <span style={{ color: theme.palette.warning.main, fontWeight: 'normal' }}>
                                {` • ${hiddenDueToImages} hidden (no images)`}
                              </span>
                            )}
                          </>
                        )}
                      </Typography>
                      
                      {isLoadingMore && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CircularProgress size={16} color="primary" />
                          <Typography 
                            variant="body2" 
                            sx={{ color: theme.palette.grey[400] }}
                          >
                            Loading...
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  )}
                </>
              )
            )}
          </>
        )}
      </Drawer>

      {/* Create Item Dialog */}
      <ItemFormDialog
        open={isCreateDialogOpen}
        onClose={() => setIsCreateDialogOpen(false)}
        endpoint={currentEndpoint}
        mode="create"
        onItemCreated={handleItemCreated}
      />
    </>
  );
};

export default DataDrawer;
